import { styled } from '@uhg-abyss/web/tools/styled';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { Suspense } from 'react';

import { Constants } from '../Constants';
import { IconBrandAndSymbol } from '../IconBrandAndSymbol';
import { IconName } from '../IconBrandAndSymbol/types';
import { LinkWithTracking } from '../LinkWithTracking/LinkWithTracking';

interface Props {
  onClick?: () => void;
  icon: string;
  text: string;
  url?: string;
  'data-testid'?: string;
  isStandardAnchor?: boolean;
  adobeChipName?: string;
  extraIcon?: boolean;
  hide?: boolean;
}

const containerStyle = {
  'abyss-flex-root': {
    alignItems: 'center',
    background: '$core.color.neutral.0',
    border: '1px solid $core.color.neutral.20',
    boxShadow: '0px 1px 4px 0px rgba(25, 25, 26, 0.16)',
    borderRadius: '8px',
    marginRight: '8px',
    '@media (min-width: 745px)': {
      paddingRight: '24px',
      paddingLeft: '16px',
      height: '42px',
    },
    '@media (max-width: 744px)': {
      paddingRight: '12px',
      paddingLeft: '4px',
      height: '40px',
      marginRight: '0px',
    },
  },
};

const ExternalLinkIcon = styled(Flex, {
  height: '24px',
  width: 'auto',
  paddingLeft: '$sm',
  alignItems: 'center',
  alignSelf: 'stretch',
});

export const Chip = ({
  onClick,
  icon,
  'data-testid': testId = '',
  text,
  url = '',
  isStandardAnchor = true,
  adobeChipName = '',
  extraIcon = false,
  hide = false,
}: Props) =>
  !hide && (
    <Flex
      css={containerStyle}
      data-auto-testid="chips-link-commonly-searched-container"
      data-testid="chips-link-commonly-searched-container"
    >
      <LinkWithTracking
        analyticsInfo={{
          location: `body:${Constants.BROWSE_FOR_CARE.HEADING}`,
          ...(adobeChipName && { name: adobeChipName }),
        }}
        css={{
          'abyss-link-root': {
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'nowrap',
            width: 'max-content',
            whiteSpace: 'nowrap',
          },
        }}
        data-auto-testid={`chips-link-${testId}`}
        data-testid={`chips-link-${testId}`}
        fontWeight="bold"
        href={url}
        isStandardAnchor={isStandardAnchor}
        onClick={onClick}
      >
        {icon ? (
          <Suspense>
            {' '}
            <IconBrandAndSymbol
              data-testid="chips-link-commonly-searched-icon"
              icon={icon as IconName}
              size={24}
              variant="twotone"
            />
          </Suspense>
        ) : null}
        {text ? (
          <Text
            color="$core.color.brand.60"
            css={{
              'abyss-text-root': {
                paddingLeft: '$xs',
                fontWeight: '600',
              },
            }}
            data-testid="chips-link-commonly-searched-text"
          >
            {text}
          </Text>
        ) : null}
        {extraIcon ? (
          <ExternalLinkIcon>
            <IconSymbol
              color="$core.color.brand.60"
              data-testid="optometrist-speciality-link"
              icon="open_in_new"
              size="18px"
            />
          </ExternalLinkIcon>
        ) : null}
      </LinkWithTracking>
    </Flex>
  );
